import React, { useEffect, useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
const bytesToMb = (bytes) => (bytes == null ? '' : Math.round(bytes / (1024 * 1024)));
const formatSpace = (mb) => { if (mb == null || mb === '') return '—'; const n = Number(mb); if (isNaN(n)) return '—'; return n >= 1024 ? (n / 1024).toFixed(1) + ' GB' : n + ' MB'; };
const inputCls = 'w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100 bg-slate-50 focus:bg-white transition-all';

const toDateInputValue = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const statusStyle = {
  active: 'bg-emerald-100 text-emerald-700',
  trial: 'bg-blue-100 text-blue-700',
  pending_approval: 'bg-amber-100 text-amber-700',
  expired: 'bg-slate-100 text-slate-600',
};

const PendingSubscriptions = () => {
  const [pending, setPending] = useState([]);
  const [userSubs, setUserSubs] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approvingId, setApprovingId] = useState(null);
  const [approveModal, setApproveModal] = useState(null);
  const [overrideDuration, setOverrideDuration] = useState('');
  const [overrideSpaceMb, setOverrideSpaceMb] = useState('');
  const [editUser, setEditUser] = useState(null);
  const [editExpiresAt, setEditExpiresAt] = useState('');
  const [editSpaceMb, setEditSpaceMb] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [selectedUserForHistory, setSelectedUserForHistory] = useState(null);

  useEffect(() => { fetchPending(); fetchUserSubs(); fetchHistory(); }, []);

  const fetchPending = async () => {
    try { const res = await axios.get(`${API_URL}/subscriptions/admin/pending`); setPending(Array.isArray(res.data) ? res.data : []); }
    catch { setPending([]); } finally { setLoading(false); }
  };
  const fetchUserSubs = async () => {
    try { const res = await axios.get(`${API_URL}/subscriptions/admin/users`); setUserSubs(Array.isArray(res.data) ? res.data : []); }
    catch { setUserSubs([]); }
  };
  const fetchHistory = async () => {
    try { const res = await axios.get(`${API_URL}/subscriptions/admin/history`); setHistory(Array.isArray(res.data) ? res.data : []); }
    catch { setHistory([]); }
  };

  const openApproveModal = (row) => { setApproveModal(row); setOverrideDuration(String(row.duration_days || '')); setOverrideSpaceMb(String(bytesToMb(row.space_bytes) || '')); };

  const handleApprove = async () => {
    if (!approveModal) return;
    setApprovingId(approveModal.id);
    try {
      const body = {};
      const d = parseInt(String(overrideDuration).replace(/\D/g, ''), 10);
      const s = parseInt(String(overrideSpaceMb).replace(/\D/g, ''), 10);
      if (!isNaN(d) && d >= 1) body.duration_days = d;
      if (!isNaN(s) && s >= 0) body.space_mb = s;
      await axios.post(`${API_URL}/subscriptions/admin/approve/${approveModal.id}`, body);
      setPending(pending.filter((p) => p.id !== approveModal.id));
      setApproveModal(null); fetchUserSubs(); fetchHistory();
    } catch (err) { alert(err.response?.data?.error || 'Failed to approve'); }
    finally { setApprovingId(null); }
  };

  const openEditModal = (u) => { setEditUser(u); setEditExpiresAt(toDateInputValue(u.expires_at)); setEditSpaceMb(u.space_mb != null ? String(u.space_mb) : ''); };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editUser) return;
    const spaceVal = editSpaceMb.trim() !== '' ? parseInt(String(editSpaceMb).replace(/\D/g, ''), 10) : null;
    const hasDate = editExpiresAt.trim() !== '';
    if (!hasDate && (spaceVal == null || spaceVal < 0)) { alert('Enter expiry date and/or space to update.'); return; }
    setSavingEdit(true);
    try {
      const body = {};
      if (hasDate) body.expires_at = editExpiresAt.trim();
      if (spaceVal != null && spaceVal >= 0) body.space_mb = spaceVal;
      await axios.put(`${API_URL}/subscriptions/admin/user/${editUser.user_id}`, body);
      setEditUser(null); fetchUserSubs(); fetchHistory();
    } catch (err) { alert(err.response?.data?.error || 'Failed to update'); }
    finally { setSavingEdit(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <div className="w-12 h-12 rounded-2xl bg-violet-50 flex items-center justify-center">
          <div className="w-6 h-6 border-2 border-violet-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-bold text-slate-900">Subscriptions</h1>
        <p className="text-sm text-slate-500 mt-0.5">Manage pending requests and active user subscriptions</p>
      </div>

      {/* Pending requests */}
      <section className="space-y-3">
        <div className="flex items-center gap-2">
          <h2 className="text-sm font-bold text-slate-700">Pending Requests</h2>
          {pending.length > 0 && (
            <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-amber-100 text-amber-700">{pending.length}</span>
          )}
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-slate-100">
                  {['User', 'Plan', 'Space', 'Duration', 'Requested', ''].map((h, i) => (
                    <th key={i} className={`px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wide ${i === 5 ? 'text-right' : 'text-left'}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {pending.length === 0 ? (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-slate-400">No pending subscription requests.</td></tr>
                ) : pending.map((row) => (
                  <tr key={row.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3">
                      <p className="text-sm font-semibold text-slate-800">{row.user_name || '—'}</p>
                      <p className="text-xs text-slate-400">{row.user_phone || row.user_email || '—'}</p>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">{row.plan_name}</td>
                    <td className="px-4 py-3 text-sm text-slate-600">{formatSpace(bytesToMb(row.space_bytes))}</td>
                    <td className="px-4 py-3 text-sm text-slate-600">{row.duration_days} days</td>
                    <td className="px-4 py-3 text-xs text-slate-400">{row.created_at ? new Date(row.created_at).toLocaleString() : '—'}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => openApproveModal(row)} disabled={approvingId != null}
                        className="px-3 py-1.5 rounded-xl text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 transition-colors">
                        Approve
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* User subscriptions */}
      <section className="space-y-3">
        <h2 className="text-sm font-bold text-slate-700">User Subscriptions</h2>
        <p className="text-xs text-slate-400">Change expiry date or space for any user subscription</p>
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-slate-100">
                  {['User', 'Status / Plan', 'Space', 'Expires', 'Days left', ''].map((h, i) => (
                    <th key={i} className={`px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wide ${i === 5 ? 'text-right' : 'text-left'}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {userSubs.length === 0 ? (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-slate-400">No user subscriptions yet.</td></tr>
                ) : userSubs.map((u) => {
                  const expiresAt = u.expires_at ? new Date(u.expires_at) : null;
                  const today = new Date(); today.setHours(0, 0, 0, 0);
                  const daysLeft = expiresAt ? Math.ceil((expiresAt.getTime() - today.getTime()) / (24 * 60 * 60 * 1000)) : null;
                  return (
                    <React.Fragment key={u.user_id}>
                      <tr className={`hover:bg-slate-50 transition-colors ${selectedUserForHistory?.user_id === u.user_id ? 'bg-violet-50/50' : ''}`}>
                        <td className="px-4 py-3">
                          <p className="text-sm font-semibold text-slate-800">{u.user_name || '—'}</p>
                          <p className="text-xs text-slate-400">{u.user_phone || u.user_email || '—'}</p>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex px-2 py-0.5 text-xs font-bold rounded-full ${statusStyle[u.subscription_status] || 'bg-slate-100 text-slate-600'}`}>
                            {(u.subscription_status || '—').replace('_', ' ')}
                          </span>
                          {u.plan_name && <span className="ml-1.5 text-xs text-slate-400">{u.plan_name}</span>}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600">{formatSpace(u.space_mb)}</td>
                        <td className="px-4 py-3 text-sm text-slate-500">{u.expires_at ? new Date(u.expires_at).toLocaleDateString() : '—'}</td>
                        <td className="px-4 py-3 text-sm">
                          {daysLeft !== null ? (
                            <span className={`font-medium ${daysLeft < 0 ? 'text-rose-600' : daysLeft <= 7 ? 'text-amber-600' : 'text-slate-600'}`}>
                              {daysLeft < 0 ? `Expired ${Math.abs(daysLeft)}d ago` : `${daysLeft}d`}
                            </span>
                          ) : '—'}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="inline-flex items-center gap-1">
                            <button onClick={() => setSelectedUserForHistory(selectedUserForHistory?.user_id === u.user_id ? null : { user_id: u.user_id, user_name: u.user_name })}
                              className="px-2.5 py-1.5 rounded-xl text-xs font-medium border border-slate-200 text-slate-500 hover:bg-slate-50 transition-colors">
                              {selectedUserForHistory?.user_id === u.user_id ? 'Hide' : 'History'}
                            </button>
                            <button onClick={() => openEditModal(u)}
                              className="p-1.5 rounded-lg text-violet-600 hover:bg-violet-50 transition-colors" title="Edit">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                      {selectedUserForHistory?.user_id === u.user_id && (
                        <tr>
                          <td colSpan={6} className="px-4 py-3 bg-slate-50">
                            <p className="text-xs font-semibold text-slate-600 mb-2">History for {selectedUserForHistory.user_name}</p>
                            <div className="overflow-x-auto rounded-xl border border-slate-200">
                              <table className="min-w-full bg-white text-xs">
                                <thead>
                                  <tr className="border-b border-slate-100">
                                    {['Plan', 'Status', 'Space', 'Started', 'Expires'].map((h) => (
                                      <th key={h} className="px-3 py-2 text-left text-slate-400 uppercase font-semibold tracking-wide">{h}</th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-50">
                                  {history.filter(h => h.user_id === selectedUserForHistory.user_id).length === 0 ? (
                                    <tr><td colSpan={5} className="px-3 py-4 text-center text-slate-400">No history.</td></tr>
                                  ) : history.filter(h => h.user_id === selectedUserForHistory.user_id).map((h) => (
                                    <tr key={h.id} className="hover:bg-slate-50">
                                      <td className="px-3 py-2 text-slate-800 font-medium">{h.plan_name || '—'}</td>
                                      <td className="px-3 py-2">
                                        <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold ${statusStyle[h.status] || 'bg-slate-100 text-slate-600'}`}>
                                          {h.status.replace('_', ' ')}
                                        </span>
                                      </td>
                                      <td className="px-3 py-2 text-slate-600">{formatSpace(h.space_mb)}</td>
                                      <td className="px-3 py-2 text-slate-500">{h.started_at ? new Date(h.started_at).toLocaleDateString(undefined, { dateStyle: 'medium' }) : '—'}</td>
                                      <td className="px-3 py-2 text-slate-500">{h.expires_at ? new Date(h.expires_at).toLocaleDateString(undefined, { dateStyle: 'medium' }) : '—'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Approve modal */}
      {approveModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setApproveModal(null)} />
          <div className="relative bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-base font-bold text-slate-900 mb-1">Approve subscription</h3>
            <p className="text-sm text-slate-500 mb-4">{approveModal.user_name} · {approveModal.plan_name} (default: {formatSpace(bytesToMb(approveModal.space_bytes))}, {approveModal.duration_days} days)</p>
            <div className="space-y-3 mb-4">
              {[
                { label: 'Override duration (days)', val: overrideDuration, setter: setOverrideDuration, placeholder: 'Leave blank for plan default' },
                { label: 'Override space (MB)', val: overrideSpaceMb, setter: setOverrideSpaceMb, placeholder: 'Leave blank for plan default' },
              ].map(({ label, val, setter, placeholder }) => (
                <div key={label}>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">{label}</label>
                  <input type="text" inputMode="numeric" value={val} onChange={(e) => setter(e.target.value)} className={inputCls} placeholder={placeholder} />
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={() => setApproveModal(null)} className="flex-1 px-4 py-2 border border-slate-200 rounded-xl text-slate-700 hover:bg-slate-50 text-sm font-medium">Cancel</button>
              <button type="button" onClick={handleApprove} disabled={approvingId != null}
                className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 disabled:opacity-50 text-sm font-semibold">
                {approvingId != null ? 'Approving…' : 'Approve'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit user subscription modal */}
      {editUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setEditUser(null)} />
          <div className="relative bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-base font-bold text-slate-900 mb-1">Change duration or space</h3>
            <p className="text-sm text-slate-500 mb-1">{editUser.user_name} · {editUser.subscription_status}{editUser.plan_name ? ` · ${editUser.plan_name}` : ''}</p>
            {editUser.expires_at && (
              <p className="text-xs text-slate-400 mb-4">Current expiry: <strong className="text-slate-600">{new Date(editUser.expires_at).toLocaleDateString(undefined, { dateStyle: 'medium' })}</strong></p>
            )}
            <form onSubmit={handleSaveEdit} className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">New expiry date</label>
                <input type="date" value={editExpiresAt} onChange={(e) => setEditExpiresAt(e.target.value)} min={toDateInputValue(new Date())} className={inputCls} />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Space (MB)</label>
                <input type="text" inputMode="numeric" value={editSpaceMb} onChange={(e) => setEditSpaceMb(e.target.value)}
                  className={inputCls} placeholder={editUser.space_mb != null ? String(editUser.space_mb) : 'Enter MB'} />
              </div>
              <div className="flex gap-2 pt-1">
                <button type="button" onClick={() => setEditUser(null)} className="flex-1 px-4 py-2 border border-slate-200 rounded-xl text-slate-700 hover:bg-slate-50 text-sm font-medium">Cancel</button>
                <button type="submit" disabled={savingEdit}
                  className="flex-1 px-4 py-2 rounded-xl text-sm font-semibold text-white disabled:opacity-50"
                  style={{ background: 'linear-gradient(135deg, #7c3aed, #4f46e5)' }}>
                  {savingEdit ? 'Saving…' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PendingSubscriptions;
